/**
 * CoinEarnChip — v146.42
 *
 * "Earn N coins" pill for storefront CTAs. Renders nothing when the
 * computed coin amount is zero, so callers can drop it in
 * unconditionally.
 *
 * When `currentBalance > 0` is passed (signed-in visitor who already
 * holds coins with this creator), the label shifts to show the running
 * total — "Balance 200 → 250 coins" — so returning buyers see the
 * loyalty flywheel building up.
 *
 * Sizes:
 *   sm — inline chip beside a Buy button (default)
 *   md — full-width strip below a big Checkout CTA
 */
import { Sparkles } from "lucide-react";

export default function CoinEarnChip({
  coins,
  currentBalance = 0,
  coinName = "coins",
  size = "sm",
  className = "",
  testId,
}) {
  const amount = Math.max(0, Number(coins) || 0);
  if (amount <= 0) return null;
  const bal = Math.max(0, Number(currentBalance) || 0);
  const hasBalance = bal > 0;
  const total = bal + amount;

  if (size === "md") {
    return (
      <div
        className={`w-full border-2 border-ink bg-sun px-3 py-1.5 flex items-center justify-center gap-1.5 shadow-brutalSm ${className}`}
        data-testid={testId || "coin-earn-strip"}
      >
        <Sparkles className="w-3.5 h-3.5" strokeWidth={3} />
        {hasBalance ? (
          <span className="text-[11px] font-black uppercase tracking-tight">
            Balance {bal} → {total} {coinName} after this
          </span>
        ) : (
          <span className="text-[11px] font-black uppercase tracking-tight">
            Earn {amount} {coinName} on this purchase
          </span>
        )}
      </div>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 border border-ink bg-sun text-ink px-1.5 py-0.5 text-[10px] font-black uppercase tracking-tight whitespace-nowrap ${className}`}
      data-testid={testId || "coin-earn-chip"}
      title={hasBalance ? `Current balance: ${bal} ${coinName}` : undefined}
    >
      <Sparkles className="w-2.5 h-2.5" strokeWidth={3} />
      {hasBalance ? (
        <>
          <span>{bal}</span>
          <span aria-hidden>→</span>
          <span>{total}</span>
          <span>{coinName}</span>
        </>
      ) : (
        <>+{amount} {coinName}</>
      )}
    </span>
  );
}
